import root from 'window-or-global';

const isExisty = (value) => {
    return value !== null && value !== undefined;
};

const isEmpty = (value) => {
    return value === '';
};

const matchRegexp = (value, regexp) => {
    return !isExisty(value) || isEmpty(value) || regexp.test(value);
};

// Prevent scripting and bad actors. Prevents (, ), \, ", +, *
export const hasBadCharacters = (value) => {
    return /(\(|\)|\\|"|\+\*)/gm.test(value);
}

const validations = {
    date(dateValue) {
        const dateNoSpace = dateValue.replace(/\s/g, '');
        const safeDateValue = encodeURI(dateNoSpace);
        let valid = true;
        if (root.isNaN(Date.parse(safeDateValue)) || safeDateValue.length <= 6) {
            valid = false;
        } else {
            const now = new Date();
            const nowYear = now.getFullYear();
            const date = new Date(safeDateValue);
            const dateYear = date.getFullYear();
            valid = matchRegexp(dateYear, /^(19|20|21)\d{2}$/);
            if (valid && (!date || nowYear - dateYear > 100)) {
                //date is invalid or not recent
                valid = false;
            }
        }
        return {
            valid,
            error: ['errors.com.epicgames.validation.invalid.date']
        };
    },
    email(value) {
        const valid = matchRegexp(value,
            //eslint-disable-next-line
            /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i
        );
        return {
            valid,
            error: ['errors.com.epicgames.validation.invalid.email']
        };
    },
    url(value) {
        const valid = matchRegexp(value,
            //eslint-disable-next-line
            /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,12}(:[0-9]{1,12})?(\/.*)?$/i
        );
        return {
            valid,
            error: ['errors.com.epicgames.validation.invalid.url']
        };
    },
    /*
     * Validates the provided input is a 10-digit phone number.
     * Supports several formats, including:
     * 1234567890
     * 123-456-7890
     * 123 456 7890
     * (123) 456 7890
     * (123) 456-7890
     */
    'phone-ten-digit'(value) {
        const valid = matchRegexp(value, /^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/);
        return {
            valid,
            error: ['errors.com.epicgames.validation.invalid.phone_ten_digit']
        };
    },
    /*
     * Validates the provided input is a valid phone number.
     * Contains 5 main capture groups for validation:
     * 1. Country Code
     * 2. Area Code
     * 3. Exchange
     * 4. Subscriber No.
     * 5. Extension No.
     *
     * Supports several formats, including:
     * 1234567890
     * 123-456-7890
     * 123 456 7890
     * (123) 456 7890
     * (123) 456-7890
     * +1 (123) 456-7890
     * +01 (123) 456-7890
     * +01 (123) 456-7890 x2345
     */
    phone(value) {
        const valid = matchRegexp(value, /^(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?$/);
        return {
            valid,
            error: ['errors.com.epicgames.validation.invalid.phone']
        };
    },
    'multi-url'(value) {
        const urls = value.split(/[\s,]+/);
        let valid = urls && urls.length > 0;
        if (valid) {
            urls.forEach(url => {
                if (valid) {
                    valid = matchRegexp(url,
                        //eslint-disable-next-line
                        /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i
                    );
                }
            });
        }
        return {
            valid,
            error: ['errors.com.epicgames.validation.invalid.url']
        };
    },
    numeric(value) {
        if (typeof value === 'number') {
            return true;
        }
        const valid = matchRegexp(value, /^[-+]?[0-9,]*\.?[0-9]+([eE][-+]?[0-9]+)?$/);
        return {
            valid,
            error: ['errors.com.epicgames.validation.invalid.number']
        };
    },
    alpha(value) {
        const valid = matchRegexp(value, /^[A-Z]+$/i);
        return {
            valid,
            error: ['errors.com.epicgames.validation.invalid.alpha']
        };
    },
    alphanumeric(value) {
        const valid = matchRegexp(value, /^[0-9A-Z]+$/i);
        return {
            valid,
            error: ['errors.com.epicgames.validation.invalid.alphanum']
        };
    },
    int(value) {
        const valid = matchRegexp(value, /^(?:[-+]?(?:0|[1-9]\d*))$/);
        return {
            valid,
            error: ['errors.com.epicgames.validation.invalid.int']
        };
    }
};

export default validations;
